import { getLegalCollections } from 'lib/api/legalCollections';
import { LegalCollection } from 'lib/api/legalCollections/types';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import CreateLegalCollection from './components/CreateLegalCollection';
import LegalCollectionDetails from './components/LegalCollectionDetails';

const LegalCollections: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const PAGE_SIZE = 10;
  const [folio, setFolio] = useState('');
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [stakeholderName, setStakeholderName] = useState('');
  const [filteredLegalCollections, setFilteredLegalCollections] = useState<
    LegalCollection[]
  >([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedLegalCollection, setSelectedLegalCollection] =
    useState<LegalCollection | null>(null);
  const { data, isLoading } = getLegalCollections(dispatch);

  const triggerFilter = async () => {
    if (!data?.legalCollections) return;
    let newFilteredData = [...data.legalCollections];
    if (folio)
      newFilteredData = newFilteredData.filter(
        (document) => document.folio === folio,
      );
    if (businessIdentifier)
      newFilteredData = newFilteredData.filter(
        (document) => document.businessIdentifier === businessIdentifier,
      );
    if (businessName)
      newFilteredData = newFilteredData.filter(
        (document) => document.businessName === businessName,
      );
    if (stakeholderIdentifier)
      newFilteredData = newFilteredData.filter(
        (document) => document.stakeholderIdentifier === stakeholderIdentifier,
      );
    if (stakeholderName)
      newFilteredData = newFilteredData.filter(
        (document) => document.stakeholderName === stakeholderName,
      );
    setFilteredLegalCollections([...newFilteredData]);
  };

  const filters: Filter[] = [
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
    {
      label: 'Rut Emisor',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Nombre Emisor',
      type: 'localAutocomplete',
      value: businessName,
      setter: setBusinessName,
      options: data?.legalCollections.map((legalCollection) => ({
        label: legalCollection.businessName,
        value: legalCollection.businessName,
      })),
    },
    {
      label: 'Rut Deudor',
      type: 'text',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      label: 'Nombre Deudor',
      type: 'localAutocomplete',
      value: stakeholderName,
      setter: setStakeholderName,
      options: data?.legalCollections.map((legalCollection) => ({
        label: legalCollection.stakeholderName,
        value: legalCollection.stakeholderName,
      })),
    },
  ];

  const handleClickRow = (id: number) => {
    const legalCollection = filteredLegalCollections.find(
      (legalCollection) => legalCollection.id === id,
    );
    if (!legalCollection) return;
    setSelectedLegalCollection(legalCollection);
    setOpenDetails(true);
  };

  useEffect(() => {
    if (data) {
      setFilteredLegalCollections(data?.legalCollections || []);
    }
  }, [data]);

  const canCreate = validatePermissions(
    user,
    'financing_legal_collection_create',
  );

  if (isLoading) return <CenteredCircularProgress type="layout" />;
  return (
    <div>
      <HeaderWithActions
        title="Acá encontrarás todos los procesos de cobranza judicial."
        variant={'body1'}
        buttons={[
          {
            name: 'Buscar',
            action: () => {},
            color: 'primary',
            customButton: (
              <GlobalSearcher
                filters={filters}
                triggerSearch={triggerFilter}
                dispatch={dispatch}
                disableSearch={isLoading}
              />
            ),
          },
          {
            name: 'Crear',
            action: () => setOpenCreate(true),
            disabled: !canCreate,
            color: 'primary',
          },
        ]}
      />
      <EnhancedTable
        dataHeaders={[
          { key: 'id', label: 'ID' },
          { key: 'businessName', label: 'Emisor' },
          { key: 'businessIdentifier', label: 'Rut emisor' },
          { key: 'stakeholderName', label: 'Deudor' },
          { key: 'stakeholderIdentifier', label: 'Rut deudor' },
          { key: 'folio', label: 'Folio' },
          { key: 'amount', label: 'Monto', type: 'money' },
          { key: 'startDate', label: 'Fecha inicio', type: 'date' },
          { key: 'status', label: 'Estado', type: 'status' },
        ]}
        exportToFile="xlsx"
        data={filteredLegalCollections}
        rowsPerPageDefault={PAGE_SIZE}
        lastElement
        handleClickRow={handleClickRow}
      />
      {openCreate && (
        <CreateLegalCollection open={openCreate} setOpen={setOpenCreate} />
      )}
      {openDetails && selectedLegalCollection && (
        <LegalCollectionDetails
          open={openDetails}
          setOpen={setOpenDetails}
          legalCollection={selectedLegalCollection}
        />
      )}
    </div>
  );
};

export default LegalCollections;
